import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { FiLock, FiEye, FiEyeOff, FiCheckCircle, FiAlertCircle, FiArrowLeft, FiCheck } from 'react-icons/fi';
import companyLogo from '../assets/logo.png';
import toast from 'react-hot-toast';
import { baseURL } from '../finance/api/client';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [errorMessage, setErrorMessage] = useState('');

  // Password requirements
  const isMinLength = newPassword.length >= 6;
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;

  useEffect(() => {
    let timer;
    if (success && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (success && countdown === 0) {
      navigate('/login');
    }
    return () => clearTimeout(timer);
  }, [success, countdown, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      setErrorMessage('Reset token is missing from the URL. Please request a new password reset link.');
      toast.error('Missing reset token');
      return;
    }

    if (!isMinLength) {
      setErrorMessage('Password must be at least 6 characters long.');
      toast.error('Password too short');
      return;
    }

    if (!passwordsMatch) {
      setErrorMessage('Passwords do not match.');
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const response = await axios.post(`${baseURL}/auth/reset-password`, {
        token: token.trim(),
        newPassword,
        confirmPassword,
      });

      if (response.status === 200) {
        setSuccess(true);
        toast.success('Password has been reset successfully!');
      } else {
        toast.error('Failed to reset password. Please try again.');
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to reset password. The link may have expired or already been used.';
      setErrorMessage(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100/70 font-sans p-3 sm:p-6 lg:p-8">
      <div className="flex flex-col lg:flex-row w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-100 my-auto">
        {/* Branding Section — Top Banner on Mobile, Left Column on Laptop */}
        <div className="w-full lg:w-1/2 bg-slate-900 flex flex-col items-center justify-center py-8 px-6 sm:py-10 sm:px-8 lg:p-12 text-white relative overflow-hidden shrink-0">
          <div className="absolute top-[-20%] left-[-10%] w-72 sm:w-96 h-72 sm:h-96 rounded-full bg-blue-600/20 blur-3xl mix-blend-screen pointer-events-none"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-60 sm:w-80 h-60 sm:h-80 rounded-full bg-indigo-500/20 blur-3xl mix-blend-screen pointer-events-none"></div>

          <div className="relative z-10 flex flex-col items-center justify-center my-auto">
            <img
              src={companyLogo}
              alt="Orakris Logo"
              className="w-44 sm:w-56 lg:w-64 h-auto object-contain drop-shadow-2xl transform hover:scale-105 transition-transform duration-500 ease-out"
            />
          </div>

          <div className="relative z-10 w-full text-center mt-auto pt-6 hidden lg:block">
            <p className="text-sm text-slate-400/80 font-medium tracking-wide">© 2026 Orakris. All rights reserved.</p>
          </div>
        </div>

        {/* Right Side - Reset Password Form */}
        <div className="w-full lg:w-1/2 flex flex-col justify-center p-6 sm:p-10 lg:p-16 relative bg-white">
          <div className="max-w-md w-full mx-auto">
            <div className="mb-8 sm:mb-10 text-center lg:text-left">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1.5 tracking-tight">Create New Password</h2>
              <p className="text-slate-500 text-sm sm:text-base leading-relaxed">
                Your new password must be different from previous passwords and at least 6 characters long.
              </p>
            </div>

            {!token ? (
              <div className="space-y-6">
                <div className="p-5 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-sm space-y-2">
                  <div className="flex items-center gap-2 font-semibold text-amber-900">
                    <FiAlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    Invalid or Missing Token
                  </div>
                  <p className="text-amber-700 leading-relaxed">
                    This password reset link is invalid or incomplete. Please request a new password reset link.
                  </p>
                </div>

                <Link
                  to="/forgot-password"
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                >
                  Request New Reset Link
                </Link>

                <div className="text-center pt-2">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                  >
                    <FiArrowLeft className="w-4 h-4" />
                    Back to Sign In
                  </Link>
                </div>
              </div>
            ) : success ? (
              <div className="space-y-6">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                    <FiCheckCircle className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Password Reset Successful!</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Your password has been securely updated. You can now use your new password to sign in.
                  </p>
                  <p className="text-xs text-slate-500 mt-4">
                    Redirecting to sign-in page in <span className="font-bold text-blue-600">{countdown}</span> seconds...
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                >
                  Sign In Now
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {errorMessage && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 text-red-700 text-sm">
                    <FiAlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-500" />
                    <div className="space-y-1">
                      <span>{errorMessage}</span>
                      {errorMessage.toLowerCase().includes('expired') || errorMessage.toLowerCase().includes('already been used') ? (
                        <div>
                          <Link to="/forgot-password" className="font-medium underline hover:text-red-900">
                            Request a new link here
                          </Link>
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}

                {/* New Password */}
                <div>
                  <label htmlFor="new-password" className="block text-sm font-medium text-slate-700 mb-1.5">New Password</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <FiLock className="text-slate-400" />
                    </div>
                    <input
                      id="new-password"
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="block w-full pl-10 pr-10 py-3 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors shadow-sm bg-slate-50 focus:bg-white"
                      placeholder="••••••••"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600"
                    >
                      {showNewPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div>
                  <label htmlFor="confirm-password" className="block text-sm font-medium text-slate-700 mb-1.5">Confirm New Password</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <FiLock className="text-slate-400" />
                    </div>
                    <input
                      id="confirm-password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="block w-full pl-10 pr-10 py-3 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors shadow-sm bg-slate-50 focus:bg-white"
                      placeholder="••••••••"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600"
                    >
                      {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </div>

                {/* Validation checklist */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-2 text-xs">
                  <div className={`flex items-center gap-2 ${isMinLength ? 'text-emerald-600 font-medium' : 'text-slate-500'}`}>
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center ${isMinLength ? 'bg-emerald-100' : 'bg-slate-200'}`}>
                      <FiCheck className="w-3 h-3" />
                    </div>
                    <span>At least 6 characters</span>
                  </div>
                  <div className={`flex items-center gap-2 ${passwordsMatch ? 'text-emerald-600 font-medium' : 'text-slate-500'}`}>
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordsMatch ? 'bg-emerald-100' : 'bg-slate-200'}`}>
                      <FiCheck className="w-3 h-3" />
                    </div>
                    <span>Passwords match</span>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !isMinLength || !passwordsMatch}
                  className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Updating Password...</span>
                    </div>
                  ) : (
                    'Reset Password'
                  )}
                </button>

                <div className="pt-2 text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                  >
                    <FiArrowLeft className="w-4 h-4" />
                    Back to Sign In
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
